import type { BuyOutcome } from "@highmarch/app";
import type { WyrmDef } from "../rare/wyrmApi";
import { gemPackArt, gemPackSlotHtml } from "./deals/dealsTiles";
import { purchaseFailText } from "./deals/dealsModel";
import { gemIcon } from "./hub/econArt";
import { confirmPurchase, econCue } from "./hub/purchaseFx";
import { btnHtml, motionOk } from "./kit";
import { toast } from "./toast";
import { paintWyrm } from "./wyrmUi";
import { WYRM_TOPUP, fmtGems, topUpPlan } from "./wyrmModel";

/** A gem pack as the top-up lays it out: the catalogue's pack with its value burst and VIP XP (as on the Shop sheet). */
export interface WyrmStorePack {
  readonly id: string;
  readonly name: string;
  readonly gems: number;
  readonly referencePrice: string;
  readonly value: { readonly percent: number; readonly show: boolean };
  readonly vipPoints: number;
}

/**
 * The game's real gem-pack store as the Wyrm's choice reaches it (boot/wyrmBank.ts over PurchaseService): the packs,
 * the store's own prices, and a buy that the app allows only from the hub and from this choice
 * (domain meta/shop.ts gemPacksAllowed, screen "wyrmChoice").
 */
export interface WyrmStore {
  /** The web build's test store: one confirm before a buy, and a note that nothing is charged. */
  readonly sandbox: boolean;
  packs(): readonly WyrmStorePack[];
  /** The store's localised price by pack id (the sandbox: the reference tier). */
  prices(): Promise<ReadonlyMap<string, string>>;
  buy(id: string): Promise<BuyOutcome>;
}

/**
 * The top-up over the Wyrm's choice (Kevin 2026-10-13: "add a way to open the shop for top ups"): the battle stays
 * paused under it (the choice is still open in the sim, and nothing times it out). It lays out the gem packs that cover
 * the shortfall as the Shop's own pack slots, the smallest suggested; a buy goes through the real store (the sandbox
 * asks once first). A completed purchase closes it and hands the new gems back (the choice sheet redraws, CHALLENGE
 * AGAIN lit); a cancel stays here, a failure says why and stays; Back (or the scrim) returns to the sheet unchanged.
 */
export class WyrmTopUp {
  private el: HTMLDivElement | null = null;
  private scrim: HTMLDivElement | null = null;
  private busy = false;

  constructor(private readonly parent: HTMLElement) {}

  get shown(): boolean { return this.el !== null; }

  /** `owner`: the choice sheet; the thanks toast goes with it (it is stale once the Wyrm is called back). */
  open(def: WyrmDef, store: WyrmStore, shortfall: number, owner: HTMLElement, bought: (gems: number) => void): void {
    this.hide();
    const plan = topUpPlan(store.packs(), shortfall);
    const scrim = document.createElement("div");
    scrim.className = "wyrm-topup-scrim";
    const el = document.createElement("div");
    el.className = "sheet panel wyrm-topup";
    el.setAttribute("role", "dialog");
    el.setAttribute("aria-label", WYRM_TOPUP.title);
    paintWyrm(el, def);
    el.innerHTML = `<div class="wt-head">${gemIcon(30)}<div class="wt-t"><b class="disp">${WYRM_TOPUP.title}</b><small>${WYRM_TOPUP.lead}</small></div></div>`
      + `<div class="wt-packs k-grid3">${plan.map((p) => `<div class="wt-pack${p.suggested ? " sugg" : ""}">${p.suggested ? `<span class="wt-tag">${WYRM_TOPUP.suggested}</span>` : ""}`
        + gemPackSlotHtml({ id: p.pack.id, name: p.pack.name, gems: p.pack.gems, price: p.pack.referencePrice, art: gemPackArt(p.index), value: p.pack.value, vipPoints: p.pack.vipPoints })
        + `</div>`).join("")}</div>`
      + (store.sandbox ? `<small class="wt-note">${WYRM_TOPUP.sandbox}</small>` : "")
      + btnHtml(WYRM_TOPUP.back, "dark", { className: "wt-back" });
    this.parent.append(scrim, el);
    this.el = el; this.scrim = scrim; this.busy = false;
    scrim.addEventListener("click", () => { if (!this.busy) this.hide(); });
    el.addEventListener("click", (e) => {
      const b = (e.target as HTMLElement).closest("button");
      if (!b || this.busy) return;
      if (b.classList.contains("wt-back")) this.hide();
      else if (b.dataset.pack) void this.buy(store, b.dataset.pack, owner, bought);
    });
    void store.prices().then((prices) => {
      for (const [id, price] of prices) { const amt = el.querySelector<HTMLElement>(`.gbuy[data-pack="${id}"] .k-amt`); if (amt) amt.textContent = price; }
    }).catch(() => undefined);
    if (motionOk() && typeof el.animate === "function") {
      el.animate([{ transform: "translateY(100%)" }, { transform: "none" }], { duration: 320, easing: "cubic-bezier(.2,1.1,.4,1)" });
      scrim.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 240 });
    }
  }

  private async buy(store: WyrmStore, id: string, owner: HTMLElement, bought: (gems: number) => void): Promise<void> {
    const pack = store.packs().find((p) => p.id === id);
    if (!pack) return;
    this.busy = true;
    this.el?.classList.add("busy");
    try {
      // The platform store confirms a real payment itself; the web's test store gets one confirm (as in the Shop).
      if (store.sandbox) {
        const ok = await confirmPurchase(this.parent, { shop: true, title: "TEST PURCHASE", currency: null, price: pack.referencePrice, confirm: "BUY",
          body: `<p class="egems">${gemIcon(22)} <b>${fmtGems(pack.gems)} gems</b></p><p>${WYRM_TOPUP.sandbox}.</p>` });
        if (!ok) return;
      }
      const r = await store.buy(id);
      if (!this.el) return;
      if (r.status === "granted" || r.status === "duplicate") {
        econCue("buy");
        if (r.status === "granted") toast(this.parent, WYRM_TOPUP.thanks(fmtGems(r.gems)), { owner });
        this.hide();
        bought(r.status === "granted" ? r.gems : 0);
      } else if (r.status === "pending") toast(this.parent, WYRM_TOPUP.pending);
      else if (r.status === "refused" || r.status === "failed") toast(this.parent, purchaseFailText(r.reason), { owner: this.el });
    } finally {
      this.busy = false;
      this.el?.classList.remove("busy");
    }
  }

  hide(): void { this.el?.remove(); this.scrim?.remove(); this.el = null; this.scrim = null; this.busy = false; }
}
